import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { PayPalCheckout } from "./PayPalCheckout";
import { CardCheckout } from "./CardCheckout";

export const CartDrawer = () => {
  const { items, count, subtotal, setQty, remove, isOpen, setIsOpen } = useCart();
  const lenis = useLenis();

  useEffect(() => {
    if (isOpen) lenis?.stop();
    else lenis?.start();
  }, [isOpen, lenis]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-[60] bg-ink/70 backdrop-blur-sm"
            data-testid="bag-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-coal border-l border-white/10 flex flex-col"
            data-testid="bag-drawer"
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
              <p className="text-xs uppercase tracking-[0.3em] text-gold">
                Your Bag {count > 0 && `— ${count}`}
              </p>
              <button
                onClick={() => setIsOpen(false)}
                className="text-ash hover:text-gold transition-colors duration-300"
                data-testid="bag-close-button"
                aria-label="Close bag"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-8">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-6">
                  <p className="font-serif italic text-2xl text-white" data-testid="bag-empty-message">
                    Your bag is empty.
                  </p>
                  <p className="text-sm text-ash">The collection awaits your stance.</p>
                </div>
              ) : (
                items.map((i) => (
                  <div
                    key={i.id}
                    className="flex gap-5 py-6 border-b border-white/5"
                    data-testid={`bag-item-${i.id}`}
                  >
                    <div className="w-20 h-20 shrink-0 overflow-hidden border border-white/10">
                      <img src={i.product.image} alt={i.product.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{i.product.edition}</p>
                          <p className="font-serif text-xl text-white leading-tight">{i.product.name}</p>
                        </div>
                        <button
                          onClick={() => remove(i.id)}
                          className="text-ash hover:text-gold transition-colors duration-300"
                          data-testid={`bag-remove-${i.id}`}
                          aria-label={`Remove ${i.product.name}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-3 border border-white/15 rounded-full px-3 py-1">
                          <button
                            onClick={() => setQty(i.id, i.qty - 1)}
                            className="text-ash hover:text-gold transition-colors"
                            data-testid={`bag-qty-minus-${i.id}`}
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="text-sm text-white w-4 text-center" data-testid={`bag-qty-${i.id}`}>
                            {i.qty}
                          </span>
                          <button
                            onClick={() => setQty(i.id, i.qty + 1)}
                            className="text-ash hover:text-gold transition-colors"
                            data-testid={`bag-qty-plus-${i.id}`}
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <p className="font-serif text-lg text-champagne">${(i.product.price * i.qty).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="px-8 py-6 border-t border-white/10">
                <div className="flex items-center justify-between mb-5">
                  <p className="text-xs uppercase tracking-[0.3em] text-ash">Subtotal</p>
                  <p className="font-serif text-2xl text-champagne" data-testid="bag-subtotal">
                    ${subtotal.toFixed(2)}
                  </p>
                </div>
                <PayPalCheckout
                  items={items.map((i) => ({ lookup_key: i.product.lookupKey, quantity: i.qty }))}
                />
                <CardCheckout
                  items={items.map((i) => ({ lookup_key: i.product.lookupKey, quantity: i.qty }))}
                  testid="bag-checkout-button"
                />
                <p className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-ash">
                  Secure checkout — PayPal or card
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
